/**
 * Schema Context for Field Components
 *
 * Provides the enum label map to all child components without prop drilling.
 * The map is built from the workflow schema fetched via useSchema hook.
 */

import React, { createContext, useContext, useMemo } from 'react';

import { useSchema } from '@/hooks/useSchema';
import { useStore } from '@/store/store';
import type { FieldEnumMap, SchemaOption } from '@/utils/fields';
import {
  buildEnumLabelMap,
  buildFieldOptionsMap,
  buildRequiredFieldPaths,
  formatFieldValue as formatFieldValueUtil,
  formatValue as formatValueUtil,
  optionsToEnumMap,
} from '@/utils/fields';

import { useCaseSchemaVersion } from './CaseSchemaVersionContext';

interface SchemaContextValue {
  /** Map of enum values to their human-readable labels */
  enumMap: Record<string, string>;
  /** Path-scoped enum map for field-aware formatting */
  fieldEnumMap: FieldEnumMap;
  /** Dotted paths of fields the schema marks `validation.required === true`. */
  requiredFieldPaths: Set<string>;
  /** Whether the schema is currently loading */
  isLoading: boolean;
  /** Format a value using the schema's enum labels */
  formatValue: (value: unknown) => string;
  /** Format a value using path-scoped enum mapping (only applies labels to enum fields) */
  formatFieldValue: (value: unknown, fieldId?: string, pathPrefix?: string) => string;
  /**
   * Returns true if the schema marks `<pathPrefix>.<fieldId>` (or just
   * `<fieldId>` when no prefix is given) as required. Path composition
   * matches `formatFieldValue`, so call sites already inside a
   * `FieldPathProvider` can pass just `fieldId`.
   */
  isFieldRequired: (fieldId?: string, pathPrefix?: string) => boolean;
  /**
   * The enum options the schema declares for `<pathPrefix>.<fieldId>`, in
   * schema order. Path composition matches `isFieldRequired`. Returns an empty
   * array when the field isn't an enum or the schema hasn't loaded, so callers
   * can fall back to a local list rather than render an empty dropdown.
   */
  getFieldOptions: (fieldId?: string, pathPrefix?: string) => SchemaOption[];
}

const SchemaContext = createContext<SchemaContextValue | null>(null);

// Module-level frozen empty set — stable reference for the fallback when no
// schema has loaded yet. Returning a fresh `new Set()` per render would
// break referential stability for downstream memos/effects.
const EMPTY_REQUIRED_PATHS: ReadonlySet<string> = new Set<string>();

// Same rationale as EMPTY_REQUIRED_PATHS: a stable reference for the "no
// options" answer, so consumers can pass it straight into a memo/effect dep.
const EMPTY_OPTIONS: SchemaOption[] = [];

interface SchemaProviderProps {
  children: React.ReactNode;
}

/**
 * Provider that fetches schema and provides enum label map to children.
 * Uses selectedWorkflowId from the store to fetch the appropriate schema, at the
 * case's schema version when mounted inside a case page (CaseSchemaVersionProvider).
 */
export const SchemaProvider: React.FC<SchemaProviderProps> = ({ children }) => {
  const workflowId = useStore(state => state.selectedWorkflowId);

  const schemaVersion = useCaseSchemaVersion();

  const { schema, isLoading } = useSchema(workflowId, schemaVersion);

  // Build enum map from schema, memoized to avoid rebuilding on every render
  const enumMap = useMemo(() => {
    if (!schema) return {};
    return buildEnumLabelMap(schema);
  }, [schema]);

  // Path-scoped options map — the raw enum extraction. Display formatting only
  // needs value→label, but input controls need the ordered option list, so both
  // are derived from this one walk.
  const fieldOptionsMap = useMemo(() => {
    if (!schema) return {};
    return buildFieldOptionsMap(schema);
  }, [schema]);

  // Build path-scoped enum map for field-aware formatting
  const fieldEnumMap = useMemo(() => optionsToEnumMap(fieldOptionsMap), [fieldOptionsMap]);

  // Build the set of dotted field paths the schema marks required, so callers
  // can drive the `*` indicator from the schema instead of hardcoding.
  // Frozen empty set used as the fallback so consumers receive a stable
  // reference across renders before the schema loads — matches the
  // referential-stability fix applied to `useCaseAudit`.
  const requiredFieldPaths = useMemo(() => {
    if (!schema) return EMPTY_REQUIRED_PATHS;
    return buildRequiredFieldPaths(schema);
  }, [schema]);

  // Create a bound formatValue function that uses the current enum map
  const formatValue = useMemo(() => {
    return (value: unknown) => formatValueUtil(value, enumMap);
  }, [enumMap]);

  // Create a bound formatFieldValue function that uses the path-scoped enum map
  const formatFieldValue = useMemo(() => {
    return (value: unknown, fieldId?: string, pathPrefix?: string) =>
      formatFieldValueUtil(value, fieldId, pathPrefix || '', fieldEnumMap);
  }, [fieldEnumMap]);

  // Bound required-field lookup. Path composition mirrors `formatFieldValue`
  // so consumers that already use `FieldPathContext` can just pass `fieldId`.
  const isFieldRequired = useMemo(() => {
    return (fieldId?: string, pathPrefix?: string) => {
      if (!fieldId) return false;
      const path = pathPrefix ? `${pathPrefix}.${fieldId}` : fieldId;
      return requiredFieldPaths.has(path);
    };
  }, [requiredFieldPaths]);

  // Bound option lookup. Path composition mirrors `isFieldRequired`.
  const getFieldOptions = useMemo(() => {
    return (fieldId?: string, pathPrefix?: string) => {
      if (!fieldId) return EMPTY_OPTIONS;
      const path = pathPrefix ? `${pathPrefix}.${fieldId}` : fieldId;
      return fieldOptionsMap[path] ?? EMPTY_OPTIONS;
    };
  }, [fieldOptionsMap]);

  const value: SchemaContextValue = {
    enumMap,
    fieldEnumMap,
    requiredFieldPaths: requiredFieldPaths as Set<string>,
    isLoading,
    formatValue,
    formatFieldValue,
    isFieldRequired,
    getFieldOptions,
  };

  return <SchemaContext.Provider value={value}>{children}</SchemaContext.Provider>;
};

/**
 * Hook to access the schema context with a fallback for cases where
 * the context might not be available (e.g., testing or isolated rendering).
 *
 * @returns The schema context value, or a default value with empty enum map
 */
export function useSchemaContextSafe(): SchemaContextValue {
  const context = useContext(SchemaContext);
  if (!context) {
    // Return a default context that humanizes values
    return {
      enumMap: {},
      fieldEnumMap: {},
      requiredFieldPaths: EMPTY_REQUIRED_PATHS as Set<string>,
      isLoading: false,
      formatValue: (value: unknown) => formatValueUtil(value, {}),
      formatFieldValue: (value: unknown, fieldId?: string, pathPrefix?: string) =>
        formatFieldValueUtil(value, fieldId, pathPrefix || '', {}),
      isFieldRequired: () => false,
      getFieldOptions: () => EMPTY_OPTIONS,
    };
  }
  return context;
}
